import { useMemo } from 'react';
import { User as UserIcon, Users as TeamIcon } from 'lucide-react';
import { OptionProps, components, MultiValueGenericProps } from 'react-select';

import { PortainerSelect, Option } from '@@/form-components/PortainerSelect';
import { TextTip } from '@@/Tip/TextTip';
import { Link } from '@@/Link';

interface UserTeamValue {
  Type: 'user' | 'team';
  Id: number;
  Name: string;
}

type UserTeamOption = Option<UserTeamValue>;

interface Props {
  options: UserTeamOption[];
  value: UserTeamValue[];
  onChange(value: UserTeamValue[]): void;
  isLoading?: boolean;
}

const SELECT_ALL_KEY = 'select-all';
const selectAllOption: Option<UserTeamValue | null> = {
  label: 'Select all',
  value: null,
};

export function UserTeamSelector({
  options,
  value,
  onChange,
  isLoading,
}: Props) {
  const selectOptions = useMemo<Option<UserTeamValue | null>[]>(() => {
    const baseOptions = options.map<Option<UserTeamValue | null>>((option) => ({
      ...option,
    }));

    const enabledOptions = options.filter((option) => !option.disabled);
    if (enabledOptions.length <= 1) {
      return baseOptions;
    }

    const selectedKeys = new Set(value.map(getValueKey));
    const hasUnselected = enabledOptions.some(
      (option) => !selectedKeys.has(getValueKey(option.value))
    );
    if (!hasUnselected) {
      return baseOptions;
    }

    return [selectAllOption, ...baseOptions];
  }, [options, value]);

  const hasNoOptions = !isLoading && options.length === 0;

  return (
    <>
      {hasNoOptions && (
        <TextTip color="blue" inline={false} className="mb-2">
          No users or teams available. You can create{' '}
          <Link
            to="portainer.users"
            target="_blank"
            rel="noopener noreferrer"
            data-cy="create-users-link"
          >
            users
          </Link>
          ,{' '}
          <Link
            to="portainer.teams"
            target="_blank"
            rel="noopener noreferrer"
            data-cy="create-teams-link"
          >
            teams
          </Link>
          , or configure{' '}
          <Link
            to="portainer.settings.authentication"
            target="_blank"
            rel="noopener noreferrer"
            data-cy="auth-settings-link"
          >
            external authentication
          </Link>{' '}
          to manage users and teams.
        </TextTip>
      )}
      <PortainerSelect<UserTeamValue | null>
        options={selectOptions}
        value={value}
        onChange={(selectedOptions) => {
          if (selectedOptions.some(isSelectAllValue)) {
            const enabledValues = options
              .filter((option) => !option.disabled)
              .map((option) => option.value);
            onChange(enabledValues);
            return;
          }

          onChange(selectedOptions.filter(isUserTeamValue));
        }}
        isMulti
        data-cy="user-team-selector"
        components={{
          MultiValueLabel,
          Option: OptionComponent,
        }}
        getOptionValue={(val) => (val ? getValueKey(val) : SELECT_ALL_KEY)}
        bindToBody
      />
    </>
  );
}

function OptionComponent({
  data,
  ...props
}: OptionProps<Option<UserTeamValue | null>, true>) {
  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <components.Option data={data} {...props}>
      <Label option={data} />
    </components.Option>
  );
}

function MultiValueLabel({
  data,
  ...props
}: MultiValueGenericProps<Option<UserTeamValue | null>, true>) {
  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <components.MultiValueLabel data={data} {...props}>
      <Label option={data} />
    </components.MultiValueLabel>
  );
}

function Label({ option }: { option: Option<UserTeamValue | null> }) {
  if (isSelectAllValue(option.value)) {
    return <span>{option.label}</span>;
  }

  const Icon = option.value.Type === 'user' ? UserIcon : TeamIcon;

  return (
    <div className="flex items-center gap-1">
      <Icon />
      <span>{option.value.Name}</span>
    </div>
  );
}

function getValueKey(value: UserTeamValue) {
  return `${value.Type}-${value.Id}`;
}

function isSelectAllValue(value: UserTeamValue | null): value is null {
  return value === null;
}

function isUserTeamValue(value: UserTeamValue | null): value is UserTeamValue {
  return value !== null;
}
